/* eslint-disable no-confusing-arrow */
import React from 'react';
import _has from 'lodash/has';
import {
  formatDate,
  formatNumber,
  formatCurrency,
  Text,
  AutoSavedSelectControl,
  AutoSavedFilteredSelectControl,
  Stack,
  withTranslation,
  TFunction,
} from '@piwikpro/ui-components';
import { ConfigService, errorBoundary, withInject } from '@piwikpro/platform';
import { IUser, Users } from '@piwikpro/users-crate';
import { TranslateService } from '@piwikpro/translation-crate';
import styles from './LanguageAndRegion.module.css';

const FORMAT_PREVIEW_SEPARATOR = ' • ';

export interface LanguageAndRegionProps {
  user: {
    id: IUser['id']
    attributes: Required<IUser['attributes']>
  }
  users: Users
  t: TFunction
  loggedUserId: string
  translate: TranslateService
  config: ConfigService
}
export interface LanguageAndRegionState {
  regionSearch: string
}

class LanguageAndRegion extends React.Component<LanguageAndRegionProps, LanguageAndRegionState> {
  constructor(props) {
    super(props);

    this.state = {
      regionSearch: '',
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleLanguageAndRegionSubmit = this.handleLanguageAndRegionSubmit.bind(this);
    this.handleRegionSearch = this.handleRegionSearch.bind(this);
    this.clearSearchInput = this.clearSearchInput.bind(this);
  }

  getAvailableRegions(searchValue, items) {
    const { user } = this.props;

    return items.filter(
      item => item.id !== user.attributes.language.split('-')[1]
        ? this.searchValueMatchesItem(searchValue, item)
        : item,
    );
  }

  async handleSubmit(values, {
    setSubmitting,
    setFieldValue,
  }) {
    const {
      users,
      user,
    } = this.props;

    try {
      await users.updateUser(user.id, values, { actionErrorToast: true });
      setSubmitting(false);
    } catch (err) {
      setFieldValue('language', user.attributes.language.split('-')[0]);
      setSubmitting(false);

      throw err;
    }
  }

  async handleLanguageAndRegionSubmit(values, api) {
    const {
      user,
      loggedUserId,
      translate,
    } = this.props;
    const isLoggedIn = user.id === loggedUserId;
    let newLanguage;
    if (values.language === undefined) {
      newLanguage = `${user.attributes.language.split('-')[0]}-${values.region}`;
    }
    if (values.region === undefined) {
      newLanguage = `${values.language}-${user.attributes.language.split('-')[1]}`;
    }

    if (isLoggedIn) {
      await translate.changeLanguage(newLanguage);
    }

    try {
      await this.handleSubmit({ language: newLanguage }, api);
    } catch (err) {
      if (isLoggedIn) {
        await translate.changeLanguage(user.attributes.language);
      }
    }
  }

  handleRegionSearch(e) {
    this.setState({
      regionSearch: e.target.value,
    });
  }

  clearSearchInput() {
    this.setState({
      regionSearch: '',
    });
  }

  searchValueMatchesItem(searchValue, item) {
    return _has(item, 'name')
      && item.name.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1
      && item.name.length >= searchValue.length;
  }

  render() {
    const {
      t,
      user,
      config,
    } = this.props;
    const { regionSearch } = this.state;
    const filteredRegions = this.getAvailableRegions(regionSearch, config.get('AVAILABLE_REGIONS'));
    const selectedRegion = filteredRegions.find(
      element => element.id === user.attributes.language.split('-')[1],
    );

    if (selectedRegion === undefined) {
      throw new Error(`Region "${user.attributes.language.split('-')[1]}" not found`);
    }

    const previewCurrency = selectedRegion.currency;

    return (
      <Stack vertical spacing="narrow">
        <Stack.Item previousSpacing="default">
          <Stack spacing="wide">
            <Stack.Item>
              <Stack verticalAlignment="center" spacing="narrow">
                <Stack.Item>
                  <span id="user-profile-language-label">
                    <Text color="InkLighter" as="p" size={12} dataId="TextStyle">
                      {t('users.generals.language.title')}
                    </Text>
                  </span>
                </Stack.Item>
                <Stack.Item>
                  {/* FIXME: This div is not necessary, we should use the AutoSavedSelectControl component directly */}
                  <div
                    className={styles.inlineControl}
                    role="group"
                    aria-labelledby="user-profile-language-label"
                  >
                    <AutoSavedSelectControl
                      id="user-profile-language-form"
                      key={user.id}
                      type="subdued"
                      initialValue={user.attributes.language.split('-')[0]}
                      name="language"
                      options={[{
                        id: 'user-profile-lang-field',
                        items: config.get('AVAILABLE_LANGUAGES'),
                      }]}
                      onSubmit={this.handleLanguageAndRegionSubmit}
                    />
                  </div>
                </Stack.Item>
              </Stack>
            </Stack.Item>
            <Stack.Item>
              <Stack verticalAlignment="center" spacing="narrow">
                <Stack.Item>
                  <span id="user-profile-region-label">
                    <Text color="InkLighter" as="p" size={12} dataId="TextStyle">
                      {t('users.generals.region.title')}
                    </Text>
                  </span>
                </Stack.Item>
                <Stack.Item>
                  <div
                    className={styles.inlineControl}
                    role="group"
                    aria-labelledby="user-profile-region-label"
                  >
                    <AutoSavedFilteredSelectControl
                      id="user-profile-region-form"
                      key={user.id}
                      type="subdued"
                      initialValue={user.attributes.language.split('-')[1]}
                      value={user.attributes.language.split('-')[1]}
                      options={[{
                        id: 'user-profile-region-field',
                        items: filteredRegions,
                      }]}
                      search={regionSearch}
                      onSearch={this.handleRegionSearch}
                      onOpen={this.clearSearchInput}
                      name="region"
                      onSubmit={this.handleLanguageAndRegionSubmit}
                      getSelectedName={
                        value => filteredRegions.find(option => value === option.id).name
                      }
                    />
                  </div>
                </Stack.Item>
              </Stack>
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item previousSpacing="extraNarrow">
          <Text color="InkLighter" size={12} dataId="TextStyle">
            {`${t('users.languageAndRegion.formatPreview')} `}
            {formatDate(new Date(), { locale: user.attributes.language, format: 'L LT' })}
            {FORMAT_PREVIEW_SEPARATOR}
            {formatDate(new Date(), { locale: user.attributes.language, format: 'LL LT' })}
            {FORMAT_PREVIEW_SEPARATOR}
            {formatNumber(Number(123456789), { locale: user.attributes.language })}
            {previewCurrency && (
              <>
                {FORMAT_PREVIEW_SEPARATOR}
                {formatCurrency(456789, {
                  locale: user.attributes.language,
                  currency: previewCurrency,
                })}
              </>
            )}
          </Text>
        </Stack.Item>
        <Stack.Item />
      </Stack>
    );
  }
}

export default withInject({
  users: 'UsersCrate.users',
  config: 'config',
  translate: 'TranslationCrate.translate',
})(
  withTranslation('administration')(errorBoundary(LanguageAndRegion)),
);
